import TrashIcon from '@svg/trash.svg';
import React, { useMemo } from 'react';
import { useFormState } from 'react-final-form';
import styled from 'styled-components';
import { DeepPartial } from 'utility-types';
import MuiMenuItem from '@material-ui/core/MenuItem';
import { SelectQueryColumnField } from '@monorepo/shared/componentsV2/fields/SelectQueryColumnField';
import { FieldIconType } from 'mapistry-shared';
import { AnyFieldValues } from '../types';
import { isRequired } from '../../../utils/validators';
import { IconButton } from '../../Button/IconButton';
import { VisuallyHidden } from '../../VisuallyHidden';

const Container = styled.div`
  display: flex;
`;

const StyledSelectField = styled(SelectQueryColumnField)`
  width: 25rem;
`;

const DeleteButton = styled(IconButton)`
  min-width: 2rem;
  margin-bottom: 1.3rem; /* lines this up vertically with the input elements to its left */
  font-size: 0.75rem;
`;

type SelectValue = React.ComponentProps<typeof MuiMenuItem>['value'];

export type AggregationGroupByColumnOption = {
  label: string;
  value: Exclude<SelectValue, undefined>;
  fieldIconType: FieldIconType;
};

interface AggregationGroupBySelectProps<FormValues extends AnyFieldValues> {
  canEdit: boolean;
  formFieldIndex: number;
  formFieldName: string;
  getAllowedGroupByColumnOptions: (
    formValues: DeepPartial<FormValues>,
    fieldIndex?: number,
  ) => AggregationGroupByColumnOption[];
  onDelete?: () => void;
}

export function AggregationGroupBySelect<FormValues extends AnyFieldValues>({
  canEdit,
  formFieldIndex,
  formFieldName,
  getAllowedGroupByColumnOptions,
  onDelete,
}: AggregationGroupBySelectProps<FormValues>) {
  const { values: currentFormValues } = useFormState<DeepPartial<FormValues>>();

  const groupByOptions = useMemo(
    () =>
      getAllowedGroupByColumnOptions(currentFormValues, formFieldIndex).sort(
        (op1, op2) => op1.label.localeCompare(op2.label),
      ),
    [currentFormValues, formFieldIndex, getAllowedGroupByColumnOptions],
  );

  return (
    <Container>
      <StyledSelectField
        disabled={!canEdit}
        isLabelHidden
        label={`Group by field ${formFieldIndex + 1}`}
        name={formFieldName}
        noOptionsText="There are no fields available to group by"
        options={groupByOptions}
        placeholder="Field to group by"
        required
        validate={isRequired}
      />
      {onDelete && (
        <DeleteButton onClick={onDelete}>
          <TrashIcon />
          <VisuallyHidden>Delete group by</VisuallyHidden>
        </DeleteButton>
      )}
    </Container>
  );
}
